import { useState } from "react"

import type { ToolApproval } from "@/api/models"

import { Check, ChevronDown, ChevronUp, Clock, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type ToolApprovalHistoryProps = {
  approvals: ToolApproval[]
  className?: string
}

export function ToolApprovalHistory({ approvals, className }: ToolApprovalHistoryProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const completedApprovals = approvals.filter(
    (approval) => approval.status !== "pending" && approval.status !== "cancelled"
  )

  if (completedApprovals.length === 0) {
    return null
  }

  const getStatusIcon = (status: ToolApproval["status"]) => {
    switch (status) {
      case "approved":
        return <Check className="text-success size-3" />
      case "denied":
        return <X className="text-destructive size-3" />
      case "expired":
        return <Clock className="text-muted-foreground size-3" />
      default:
        return null
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  return (
    <div className={cn("border-border bg-card/50 rounded-md border", className)}>
      <Button
        className="text-muted-foreground hover:bg-muted/50 w-full justify-between px-3 py-2 text-xs font-medium"
        size="sm"
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>Approval History ({completedApprovals.length})</span>
        {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
      </Button>

      {isExpanded && (
        <div className="border-border max-h-60 overflow-y-auto border-t">
          {completedApprovals.map((approval) => (
            <div
              key={`${approval.request_id}-${approval.updated_at}`}
              className="border-border/50 border-b p-3 last:border-b-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(approval.status)}
                    <span className="text-foreground text-xs font-medium">{approval.tool}</span>
                  </div>
                  <p className="text-muted-foreground mt-1 text-xs">{approval.title}</p>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-xs",
                    approval.status === "approved" && "border-success text-success",
                    approval.status === "denied" && "border-destructive text-destructive",
                    approval.status === "expired" && "border-border text-muted-foreground"
                  )}
                >
                  {approval.status}
                </Badge>
              </div>
              <div className="text-muted-foreground mt-2 flex items-center justify-between text-xs">
                <span>{formatTimestamp(approval.updated_at)}</span>
                {approval.decision_user && (
                  <span className="font-medium">{approval.decision_user}</span>
                )}
              </div>
              {approval.reason && (
                <p className="text-muted-foreground mt-1 text-xs italic">"{approval.reason}"</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
